import { getInstance, registerGlobalPlugins } from "@module-federation/enhanced/runtime";
import type { ModuleFederationRuntimePlugin } from "@module-federation/enhanced/runtime";

/**
 * Forces the "loaded-first" share strategy on every federation instance created after this shell boots
 * (i.e. all remotes), regardless of which strategy they were built with. Mixing strategies
 * caused a race where a "loaded-first" consumer resolved a shared singleton to an entry that was
 * registered but never loaded ("loading": null), hanging the page without an error.
 * Note: runtimePlugins in webpack.common.js (like retry.ts) are baked into the host's own federation instance only
 * and do not affect remotes.
 */
const forceLoadedFirstPlugin: ModuleFederationRuntimePlugin = {
	name: "uidispatcher-force-loaded-first",
	beforeInit(args) {
		args.userOptions.shareStrategy = "loaded-first";
		return args;
	},
};

export const setupModuleFederation = async (): Promise<void> => {
	try {
		registerGlobalPlugins([forceLoadedFirstPlugin]);
	} catch (error) {
		console.error("Failed to register global module federation plugins", error);
	}

	const instance = getInstance();
	if (!instance) {
		return;
	}

	// Preload the host's shared singletons so "loaded-first" resolution always finds them
	// already loaded before any remote registers a competing, never-loaded copy.
	await Promise.all(
		Object.keys(instance.options.shared).map(pkgName =>
			instance.loadShare(pkgName).catch((error: unknown) => {
				console.warn(`Failed to preload shared module "${pkgName}"`, error);
			}),
		),
	);
};
